// Единая сборка и исполнение payload'а отчёта.
//
// Раньше эта логика жила в двух местах почти дословно: store `ReportFromBuilder`
// (конструктор и страница отчёта, глобальный Vuex) и `Report.vue` (изолированные
// виджеты дашборда, локальный localReportState). Сравнение периодов должно работать
// в обоих, поэтому сборка вынесена сюда — иначе получилось бы три копии.
import ReportBuilderService from '@/helpers/api/reportBuilder.service'
import userReportService from '@/helpers/api/userReport.service'
import { getConfigIssues } from '@/helpers/reportValidation'

// Оформление серии — фронтовые поля, бэкенду не нужны
const VISUAL_FIELDS = ['color', 'lineWidth', 'lineStyle', 'lineTension', 'showPoints', 'fill']

/**
 * Подставляет реальный ключ ресурса вместо плейсхолдера $current_resource.
 * Мутирует переданный объект settings.
 */
export function resolveResourceParams(settings, currentBotKey) {
    const rKeys = settings?.params?.[':resource_key']
    if (!rKeys) return settings
    if (Array.isArray(rKeys)) {
        settings.params[':resource_key'] = rKeys
            .map(k => (k === '$current_resource' ? currentBotKey : k))
            .filter(Boolean)
    } else if (rKeys === '$current_resource') {
        settings.params[':resource_key'] = currentBotKey ? [currentBotKey] : []
    }
    return settings
}

/**
 * Полный payload для бэкенда из состояния отчёта (стор или localReportState).
 *
 * @param {object} state   {type, source_request, query, type_chart, visual, settings}
 * @param {object} options extraParams — дополнительные settings.params (ключи с ':'),
 *                         currentBotKey — ключ текущего ресурса для $current_resource
 */
export function buildReportPayload(state, { extraParams = null, currentBotKey = null } = {}) {
    const sourceRequest = JSON.parse(JSON.stringify(state.source_request || {}))

    if (Array.isArray(sourceRequest.requests)) {
        sourceRequest.requests = sourceRequest.requests.map(r => {
            const clean = { ...r }
            VISUAL_FIELDS.forEach(f => delete clean[f])
            return clean
        })
    }
    // formulas считаются на клиенте
    delete sourceRequest.formulas
    // внутренние ключи конструктора (_saved_type_chart и т.п.)
    for (const key of Object.keys(sourceRequest)) {
        if (key.startsWith('_')) delete sourceRequest[key]
    }
    // Source_request на бэкенде требует type_chart внутри себя
    if (!sourceRequest.type_chart && state.type_chart) {
        sourceRequest.type_chart = state.type_chart
    }

    const settings = JSON.parse(JSON.stringify(state.settings || {}))
    if (extraParams && Object.keys(extraParams).length) {
        if (!settings.params) settings.params = {}
        Object.assign(settings.params, JSON.parse(JSON.stringify(extraParams)))
    }
    resolveResourceParams(settings, currentBotKey)

    return {
        type: state.type,
        source_request: sourceRequest,
        query: state.query,
        type_chart: state.type_chart,
        visual: state.visual,
        settings
    }
}

export function isSqlPayload(payload) {
    return payload?.type === 'sql' || payload?.source_request?.type === 'sql'
}

/**
 * Выполняет отчёт (кроме SQL-режима, у него свой эндпоинт — см. fetchSqlReportData).
 * Возвращает сырой ответ бэкенда либо null, если конфиг неполный и запрос не имеет смысла.
 */
export async function fetchReportData(payload, { isCustomQuery = false, reportUuid = null, signal = null } = {}) {
    // A tariff-forbidden report is a normal, local outcome for a single widget/report view —
    // suppress the global 403 redirect-to-Home/toast so the caller can render an in-place stub.
    if (isCustomQuery && reportUuid) {
        return userReportService.execute(reportUuid, { settings: payload.settings }, { suppressAccessDenied: true, signal })
    }
    if (getConfigIssues(payload).length > 0) return null
    // report_uuid lets the backend tariff-gate a system report against the resource(s)
    // this call actually queries (see ReportTariffGate.php); absent for report-builder
    // draft previews, which have no saved report to check.
    const templatePayload = reportUuid ? { ...payload, report_uuid: reportUuid } : payload
    return ReportBuilderService.useReportTemplate({}, templatePayload, { suppressAccessDenied: true, signal })
}

/** SQL-режим: транспиляция + выполнение, ответ — плоские строки. */
export async function fetchSqlReportData(payload, { reportUuid = null, signal = null } = {}) {
    const sql = payload?.source_request?.sql
    if (!sql || !String(sql).trim()) return null
    const body = { sql, params: payload.settings?.params || {} }
    // report_uuid lets the backend tariff-gate this SQL report the same way as template
    // reports (see ReportTariffGate.php) — this endpoint is SQL reports' only data path.
    if (reportUuid) body.report_uuid = reportUuid
    return ReportBuilderService.getDataSql(body, { suppressAccessDenied: true, signal })
}

/**
 * Нормализует ответ бэкенда к {rows, total_count}: он приходит либо массивом, либо
 * {data, total_count}. `wrapped` говорит, был ли total_count в ответе вообще — иначе
 * его нельзя отличить от честного нуля и предыдущее значение затиралось бы.
 */
export function unwrapReportData(data) {
    if (data && typeof data === 'object' && !Array.isArray(data) && data.data !== undefined) {
        return {
            rows: Array.isArray(data.data) ? data.data : [],
            total_count: data.total_count || 0,
            wrapped: true
        }
    }
    return { rows: Array.isArray(data) ? data : [], total_count: 0, wrapped: false }
}
